import { useCallback, useMemo } from 'react'
import { useExport } from './useExport'
import {
  createLineExport,
  getLineExport,
  type CreateLineExportRequest,
} from '@/api/planExports'

/**
 * Hook to export plan lines for a specific plan version.
 * Uses the generic useExport hook with plan-lines-specific endpoints.
 */
export function useExportPlanLines(planId: string, versionId: string | null) {
  const createFn = useCallback(
    (request?: CreateLineExportRequest) =>
      createLineExport(planId, versionId!, request),
    [planId, versionId]
  )

  const getFn = useCallback(
    (exportId: string) => getLineExport(planId, versionId!, exportId),
    [planId, versionId]
  )

  const exportHook = useExport<CreateLineExportRequest>({
    createExportFn: createFn,
    getExportFn: getFn,
    filenamePrefix: 'plan-lines',
  })

  const { startExport } = exportHook

  // Disable export if no versionId
  const exportPlanLines = useCallback(
    (request?: CreateLineExportRequest) => {
      if (!versionId) return
      startExport(request)
    },
    [versionId, startExport]
  )

  const canExport = useMemo(() => !!versionId, [versionId])

  return {
    ...exportHook,
    exportPlanLines,
    canExport,
  }
}
